export interface PaidLabelPricingTier {
  minQuantity: number;
  maxQuantity?: number | null;
  unitPrice: number;
}

export interface PurchasableAddOn {
  addOn?: number;
  checked?: boolean;
  isOneTimePurchase?: boolean;
  selectedQuantity?: number | string | null;
  minimumQuantity?: number | null;
  actualPrice?: number | null;
  pricingTiers?: PaidLabelPricingTier[] | null;
}

export interface AddOnPurchaseRequest {
  addOns: number[];
  downloadLabelsQuantity?: number;
  orderId: number;
  paymentMethodId: number;
}

export function buildAddOnPurchaseRequest(
  addOns: PurchasableAddOn[],
  downloadLabelsAddOnType: number,
  orderId: number,
  paymentMethodId: number,
): AddOnPurchaseRequest {
  const selectedAddOns = addOns.filter((addOn) => addOn.checked);
  const paidLabelAddOn = selectedAddOns.find(
    (addOn) => addOn.addOn === downloadLabelsAddOnType,
  );
  const request: AddOnPurchaseRequest = {
    addOns: selectedAddOns.map((addOn) => Number(addOn.addOn)),
    orderId,
    paymentMethodId,
  };

  if (paidLabelAddOn) {
    request.downloadLabelsQuantity = getSelectedAddOnQuantity(paidLabelAddOn);
  }

  return request;
}

export function getMaximumAddOnQuantity(addOn: PurchasableAddOn): number {
  if (!addOn?.isOneTimePurchase || !Array.isArray(addOn.pricingTiers) || addOn.pricingTiers.length === 0) {
    return 1;
  }

  const lastTier = [...addOn.pricingTiers]
    .sort((left, right) => left.minQuantity - right.minQuantity)
    .pop();

  return Number(lastTier?.maxQuantity || 1);
}

export function getSelectedAddOnQuantity(addOn: PurchasableAddOn): number {
  if (!addOn?.isOneTimePurchase) {
    return 1;
  }

  const quantity = Number(addOn.selectedQuantity || addOn.minimumQuantity || 0);
  if (Number.isNaN(quantity)) {
    return 0;
  }

  return Math.min(getMaximumAddOnQuantity(addOn), Math.max(0, quantity));
}

export function isAddOnQuantityOverLimit(addOn: PurchasableAddOn): boolean {
  return Number(addOn?.selectedQuantity || 0) > getMaximumAddOnQuantity(addOn);
}

export function getSelectedAddOnUnitPrice(addOn: PurchasableAddOn): number {
  if (!addOn?.isOneTimePurchase || !Array.isArray(addOn.pricingTiers)) {
    return Number(addOn?.actualPrice || 0);
  }

  const quantity = getSelectedAddOnQuantity(addOn);
  const tier = addOn.pricingTiers.find((item) =>
    item.minQuantity <= quantity && (item.maxQuantity == null || item.maxQuantity >= quantity)
  );

  return Number(tier?.unitPrice || 0);
}
